import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import type { JSX } from "react";
import { usageTracker, UsageTrackerEvent } from "@volumental/analytic";
import { isValidEmail } from "@volumental/common";
import { requestClaimScanEmailMutation } from "./api/mutations/request-claim-scan-email-mutation";
import { decodeCallbackState } from "./util/scanner-callback-state";
import { Navigation } from "./navigation/navigation";
import { ApiProvider } from "./providers/api-provider";
import { ConfigurationProvider } from "./providers/configuration-provider";
import { useSessionStore, useInputStore, useViewStore, useScannerStore, usePersistedViewStore } from "./store";
import { AppMode, CurrentView } from "./types/types";

const TOKENS_WITHOUT_VALIDATION = new Set([
  "3ab5b9e1-1896-48d8-a2a4-c3921bc024d8",
  "59bc7a32-5dcb-4ad7-8b32-ed37a794ffa1",
]);

function getValue(key: string, sp: URLSearchParams): string | undefined {
  return sp.has(key) ? (sp.get(key) ?? undefined) : undefined;
}

function getSelectedMode(sp: URLSearchParams): AppMode {
  const value = getValue("mode", sp);
  if (value === "measurement") {
    return AppMode.Measurement;
  } else if (value === "sizechart") {
    return AppMode.Sizechart;
  }
  return AppMode.Recommendation;
}

const sp = new URLSearchParams(globalThis.location.search);

function encodeWebScanId(scanId: string): string {
  return btoa(`WebScan:${scanId}`);
}

function AppInner(): JSX.Element {
  const [claimScan] = useMutation(requestClaimScanEmailMutation);
  const { setSideBarOpen, setCurrentView } = useViewStore((store) => store);
  const { setEmail, clearEmail, setSubject, setPreviousPurchaseID, setScanID } = useInputStore((store) => store);
  const { applyParams } = useSessionStore((store) => store);
  const { setDisableCaptureValidation } = useScannerStore((store) => store);
  const { setMeasurementHasBeenShown } = usePersistedViewStore((store) => store);

  useEffect(() => {
    const token = getValue("token", sp);
    applyParams({
      token,
      mode: getSelectedMode(sp),
      context: getValue("context", sp),
      productID: getValue("product_id", sp),
      brandName: getValue("product_brand_name", sp),
      genderAge: getValue("product_gender_age", sp),
      productName: getValue("product_name", sp),
      chartId: getValue("chart_id", sp),
      callbackUrl: getValue("callback_url", sp),
      session: getValue("session", sp),
      sizeLocale: getValue("size_locale", sp),
      storeFinderUrl: getValue("store_finder_url", sp),
      deviceId: getValue("device_id", sp),
      widgetComponentType: getValue("component_type", sp),
      paperType: getValue("paper_type", sp),
      skipOnboarding: getValue("skip_onboarding", sp)?.toLocaleLowerCase() === "true",
    });
    const pp = getValue("previous_purchase_id", sp);
    if (pp) {
      setPreviousPurchaseID(pp);
    }
    const sID = getValue("scan_id", sp);
    if (sID) {
      setScanID(sID);
    }
    const subject = getValue("subject", sp);
    if (subject) setSubject(subject);
    if (subject && isValidEmail(subject)) setEmail(subject);
    else clearEmail();
    if (token) {
      setDisableCaptureValidation(TOKENS_WITHOUT_VALIDATION.has(token));
    }
    if (sp.get("callback_state") === "result") {
      usageTracker.track(UsageTrackerEvent.ScannerCallbackResult);
      const scanId = getValue("scanId", sp);
      if (scanId) setScanID(encodeWebScanId(scanId));
      const scannerState = decodeCallbackState(getValue("state", sp) ?? "");
      if (scannerState?.previousPurchaseId) setPreviousPurchaseID(scannerState.previousPurchaseId);
      if (scannerState?.email && isValidEmail(scannerState.email)) {
        setEmail(scannerState.email);
      }
      if (scanId && scannerState?.email) {
        // Best-effort: claim failure has no user-visible impact, so we only log.
        void claimScan({
          variables: {
            input: {
              scanID: encodeWebScanId(scanId),
              email: scannerState.email,
              previousPurchaseID: scannerState.previousPurchaseId,
            },
          },
        }).catch((error: unknown) => {
          console.error("failed to claim scan", error);
        });
      }
      setMeasurementHasBeenShown(false);
      setCurrentView(CurrentView.Result);
    } else if (sp.get("callback_state") === "abort") {
      // Fresh iframe load after scanner abort has no in-memory view state;
      // land on Intro so the underlying UI isn't FlowNotSupported.
      setCurrentView(CurrentView.Intro);
    }
    // This app does not support re-rendering; all deps are stable mount-time values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Checks the width of the sidebar to see if its visable or not.
  useEffect(() => {
    let closed = true;
    const root = document.querySelectorAll("html")[0];
    const observer = new ResizeObserver(() => {
      if (root.offsetWidth !== 0 && closed) {
        closed = false;
        setSideBarOpen(true);
        usageTracker.track(UsageTrackerEvent.OpenSidebar, {
          experience: getSelectedMode(sp),
          widget_component_type: getValue("component_type", sp),
          set_paper_type: getValue("paper_type", sp),
        });
      } else if (root.offsetWidth === 0 && !closed) {
        closed = true;
        setSideBarOpen(false);
        usageTracker.track(UsageTrackerEvent.CloseSidebar);
      }
    });
    observer.observe(root);
    return () => {
      observer.disconnect();
    };
    // This app does not support re-rendering; setSideBarOpen is a stable mount-time value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConfigurationProvider>
      <Navigation />
    </ConfigurationProvider>
  );
}

function AppMain(): JSX.Element {
  return (
    <ApiProvider>
      <AppInner />
    </ApiProvider>
  );
}

function App(): JSX.Element {
  return <AppMain />;
}

export { App };
